import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import {
  ChevronLeft, ChevronRight, Loader2, Truck, PackageCheck, Mail, CalendarDays,
} from "lucide-react";

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"];

const TYPE_META = {
  pickup: { label: "Pickup", icon: Truck, cls: "border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))]" },
  delivery: { label: "Delivery", icon: PackageCheck, cls: "border-[hsl(var(--success))]/40 bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]" },
  followup: { label: "Follow-up", icon: Mail, cls: "border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400" },
};

const dateKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const todayKey = dateKey(new Date());

function buildWeeks(monthDate) {
  const first = new Date(monthDate.getFullYear(), monthDate.getMonth(), 1);
  const monthEnd = new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 0);
  const cur = new Date(first);
  cur.setDate(1 - first.getDay()); // back up to the Sunday of the first week
  const weeks = [];
  for (let w = 0; w < 6; w++) {
    const week = [];
    for (let i = 0; i < 7; i++) { week.push(new Date(cur)); cur.setDate(cur.getDate() + 1); }
    weeks.push(week);
    if (cur > monthEnd) break;
  }
  return weeks;
}

function EventPill({ ev, onClick }) {
  const meta = TYPE_META[ev.type] || TYPE_META.pickup;
  const Icon = meta.icon;
  const eta = ev.type === "delivery" && !ev.actual;
  return (
    <button
      onClick={onClick}
      data-testid={`cal-event-${ev.type}-${ev.quote_id}`}
      title={`${meta.label}${eta ? " (ETA)" : ""} · ${ev.quote_number} · ${ev.customer_name}\n${ev.lane}`}
      className={`flex w-full items-center gap-1 truncate rounded border px-1.5 py-0.5 text-left text-[11px] font-semibold transition hover:brightness-110 ${meta.cls} ${ev.done ? "opacity-55" : ""} ${eta ? "border-dashed" : ""}`}
    >
      <Icon className="h-3 w-3 shrink-0" />
      <span className="truncate">{ev.customer_name}</span>
    </button>
  );
}

export default function CalendarPage() {
  const navigate = useNavigate();
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  const weeks = useMemo(() => buildWeeks(month), [month]);
  const rangeStart = dateKey(weeks[0][0]);
  const rangeEnd = dateKey(weeks[weeks.length - 1][6]);

  useEffect(() => {
    setLoading(true);
    api.get("/calendar/events", { params: { start: rangeStart, end: rangeEnd } })
      .then((r) => setEvents(r.data.events || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, [rangeStart, rangeEnd]);

  const byDay = useMemo(() => {
    const map = {};
    events.forEach((e) => { (map[e.date] = map[e.date] || []).push(e); });
    return map;
  }, [events]);

  const goMonth = (delta) => setMonth((m) => new Date(m.getFullYear(), m.getMonth() + delta, 1));
  const goToday = () => { const d = new Date(); setMonth(new Date(d.getFullYear(), d.getMonth(), 1)); };
  const openEvent = (ev) => { if (ev.quote_id) navigate(`/quotes/${ev.quote_id}`); };

  return (
    <div className="flex h-[calc(100vh-7rem)] flex-col gap-3 animate-fade-up" data-testid="calendar-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-heading text-2xl font-black tracking-tighter">
            <CalendarDays className="h-6 w-6 text-[hsl(var(--primary))]" /> Ops Calendar
          </h1>
          <p className="text-sm text-muted-foreground">Pickups, delivery ETAs &amp; follow-ups across your booked lanes</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 rounded-lg border border-border bg-card p-1">
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => goMonth(-1)} data-testid="cal-prev"><ChevronLeft className="h-4 w-4" /></Button>
            <span className="min-w-[9.5rem] text-center font-heading text-sm font-bold" data-testid="cal-month-label">{MONTHS[month.getMonth()]} {month.getFullYear()}</span>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => goMonth(1)} data-testid="cal-next"><ChevronRight className="h-4 w-4" /></Button>
          </div>
          <Button size="sm" variant="outline" onClick={goToday} data-testid="cal-today">Today</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-4 text-xs">
        {Object.entries(TYPE_META).map(([k, m]) => (
          <span key={k} className="flex items-center gap-1.5 font-semibold text-muted-foreground">
            <m.icon className="h-3.5 w-3.5" /> {m.label}
          </span>
        ))}
        <span className="flex items-center gap-1.5 text-muted-foreground/70">
          <span className="inline-block h-3 w-4 rounded border border-dashed border-muted-foreground/60" /> = estimated
        </span>
      </div>

      <div className="relative flex-1 overflow-hidden rounded-lg border border-border bg-card">
        {loading && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-card/70">
            <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--primary))]" />
          </div>
        )}
        <div className="grid grid-cols-7 border-b border-border bg-secondary/40">
          {WEEKDAYS.map((d) => (
            <div key={d} className="px-2 py-2 text-center text-[11px] font-bold uppercase tracking-wide text-muted-foreground">{d}</div>
          ))}
        </div>
        <div className="grid h-[calc(100%-2.25rem)] grid-rows-[repeat(auto-fit,minmax(0,1fr))]">
          {weeks.map((week, wi) => (
            <div key={wi} className="grid grid-cols-7 border-b border-border last:border-b-0">
              {week.map((day) => {
                const key = dateKey(day);
                const inMonth = day.getMonth() === month.getMonth();
                const isToday = key === todayKey;
                const dayEvents = byDay[key] || [];
                return (
                  <div
                    key={key}
                    data-testid={`cal-day-${key}`}
                    className={`flex min-w-0 flex-col gap-1 border-r border-border p-1.5 last:border-r-0 ${inMonth ? "" : "bg-secondary/20"}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold ${isToday ? "bg-[hsl(var(--primary))] text-white" : inMonth ? "text-foreground" : "text-muted-foreground/50"}`}>
                        {day.getDate()}
                      </span>
                      {dayEvents.length > 3 && <span className="text-[10px] font-semibold text-muted-foreground">{dayEvents.length}</span>}
                    </div>
                    <div className="flex min-h-0 flex-col gap-0.5 overflow-y-auto">
                      {dayEvents.slice(0, 4).map((ev, i) => (
                        <EventPill key={`${ev.quote_id}-${ev.type}-${i}`} ev={ev} onClick={() => openEvent(ev)} />
                      ))}
                      {dayEvents.length > 4 && (
                        <span className="px-1 text-[10px] font-semibold text-muted-foreground">+{dayEvents.length - 4} more</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        {!loading && events.length === 0 && (
          <div className="pointer-events-none absolute inset-x-0 bottom-4 z-10 text-center text-sm text-muted-foreground" data-testid="cal-empty">
            No scheduled pickups, deliveries or follow-ups this month.
          </div>
        )}
      </div>
    </div>
  );
}
